import './headless-combobox.css';

import { Combobox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { forwardRef, Fragment, useCallback, useState } from 'react';

import { useTranslation } from '@/hooks/useTranslation';

export interface HeadlessComboboxOption {
    key: string;
    label: string;
    value: any;
}

export interface HeadlessComboboxProps {
    id?: string;
    options: HeadlessComboboxOption[];
    value?: HeadlessComboboxOption | null;
    onChange: (option: HeadlessComboboxOption | null) => void;
    onInputChange?: (value: string) => void;
    placeholder?: string;
    isDisabled?: boolean;
    isLoading?: boolean;
    filterOption?: (option: HeadlessComboboxOption, inputValue: string) => boolean;
    className?: string;
    inputClassName?: string;
    optionsClassName?: string;
    optionClassName?: string;
    renderOption?: (option: HeadlessComboboxOption, { selected, active }: { selected: boolean; active: boolean }) => React.ReactNode;
    renderInput?: (props: any) => React.ReactNode;
    renderButton?: (props: any) => React.ReactNode;
    renderOptions?: (props: any) => React.ReactNode;
    onInputClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
}

export const HeadlessCombobox = forwardRef<HTMLDivElement, HeadlessComboboxProps>(
    (
        {
            options = [],
            value,
            onChange,
            onInputChange,
            placeholder = '',
            isDisabled = false,
            isLoading = false,
            filterOption = (option, inputValue) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase()),
            className = '',
            inputClassName = '',
            optionsClassName = '',
            optionClassName = '',
            renderInput,
            renderButton,
            renderOptions,
            renderOption,
            onInputClick
        },
        ref
    ) => {
        const [query, setQuery] = useState('');
        const { t } = useTranslation();
        const filteredOptions = options.filter((option) => filterOption(option, query));

        const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = event.target.value;
            setQuery(inputValue);
            onInputChange?.(inputValue);
        }, [onInputChange]);

        const handleOptionSelect = useCallback((option: HeadlessComboboxOption | null) => {
            onChange(option);
            setQuery('');
        }, [onChange]);

        const defaultRenderInput = (props: any) => (
            <Combobox.Input
                {...props}
                className={`input bordered-input tw-pl-3 tw-pr-7 tw-flex tw-w-full tw-py-2 tw-text-t-sm tw-h-9
                    tw-ring-offset-background disabled:tw-cursor-not-allowed disabled:tw-opacity-50 focus-visible:!tw-border-primary-focus tw-h-[36px] tw-bg-white ${inputClassName}`}
                displayValue={(option: HeadlessComboboxOption) => option?.label || ''}
                onChange={handleInputChange}
                onClick={(e) => onInputClick?.(e)}
                placeholder={placeholder}
            />
        );

        const defaultRenderButton = () => (
            <Combobox.Button className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-2">
                {isLoading ? (
                    <div className="tw-animate-spin tw-h-4 tw-w-4 tw-border-2 tw-border-gray-300 tw-border-t-gray-600 tw-rounded-full" />
                ) : (
                    <ChevronUpDownIcon className="tw-h-5 tw-w-5 tw-text-t-inactive" aria-hidden="true" />
                )}
            </Combobox.Button>
        );

        const defaultRenderOptions = (props: any) => (
            <Transition
                as={Fragment}
                leave="tw-transition tw-duration-100 tw-ease-in"
                leaveFrom="tw-opacity-100"
                leaveTo="tw-opacity-0"
                afterLeave={() => setQuery('')}
            >
                <Combobox.Options
                    {...props}
                    className={`headless-combobox-content tw-absolute tw-z-[1060] tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-md tw-bg-white tw-text-t-sm
                        tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none ${optionsClassName}`}
                >
                    {filteredOptions.length === 0 && query !== '' ? (
                        <div className="tw-relative tw-cursor-default tw-select-none tw-p-2 tw-text-t-inactive">
                            {t('Nothing found.')}
                        </div>
                    ) : (
                        filteredOptions.map((option) => (
                            <Combobox.Option
                                key={option.key}
                                value={option}
                                className={({ active, selected }) =>
                                    `headless-combobox-option tw-relative tw-cursor-default tw-select-none tw-p-2 tw-text-t-primary tw-w-full ${
                                        active ? 'tw-bg-blue-50' : ''
                                    } ${selected ? 'tw-bg-blue-100 headless-combobox-option-selected' : ''} ${optionClassName}`
                                }
                            >
                                {({ selected, active }) => (
                                    <span className={`${selected ? 'tw-font-medium' : ''}`}>
                                        {renderOption ? renderOption(option, { selected, active }) : option.label}
                                    </span>
                                )}
                            </Combobox.Option>
                        ))
                    )}
                </Combobox.Options>
            </Transition>
        );

        return (
            <div ref={ref} className={`tw-relative ${className}`}>
                <Combobox value={value} onChange={handleOptionSelect} disabled={isDisabled}>
                    <div className="tw-relative">
                        <div className={`headless-combobox-trigger tw-relative tw-w-full tw-cursor-default tw-overflow-hidden tw-bg-transparent tw-text-left tw-min-h-[36px] tw-flex tw-items-center
                            ${isDisabled ? 'tw-cursor-not-allowed tw-opacity-50' : ''}`}>
                            {renderInput ? renderInput({}) : defaultRenderInput({})}
                            {renderButton ? renderButton({}) : defaultRenderButton()}
                        </div>
                        {renderOptions ? renderOptions({}) : defaultRenderOptions({})}
                    </div>
                </Combobox>
            </div>
        );
    }
);

HeadlessCombobox.displayName = 'HeadlessCombobox';
